import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Supabase email confirmation typically uses URL hash fragments
        // Format: #access_token=...&refresh_token=...&type=signup
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams?.get('access_token');
        const refreshToken = hashParams?.get('refresh_token');
        const type = hashParams?.get('type');

        // Also check query parameters (some configurations use query params)
        const token = searchParams?.get('token') || hashParams?.get('token');
        const queryType = searchParams?.get('type') || type;

        // If we have tokens in the hash, set the session
        if (accessToken && refreshToken) {
          const { data, error } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Session error:', error);
            setStatus('error');
            setMessage(error?.message || 'Failed to verify email. Please try signing in.');
            setTimeout(() => {
              navigate('/institute-login');
            }, 3000);
            return;
          }

          // Success - email confirmed
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to sign in...');

          // Sign out immediately after confirmation (user needs to sign in)
          await supabase?.auth?.signOut();

          // Redirect to sign-in page after 2 seconds
          setTimeout(() => {
            navigate('/institute-login');
          }, 2000);
        } 
        // If we have a token parameter, try OTP verification
        else if (token && (queryType === 'signup' || queryType === 'email')) {
          const { data, error } = await supabase?.auth?.verifyOtp({
            token_hash: token,
            type: 'email'
          });

          if (error) {
            console.error('Email confirmation error:', error);
            setStatus('error');
            setMessage(error?.message || 'Failed to verify email. Please try again.');
            setTimeout(() => {
              navigate('/institute-login');
            }, 3000);
            return;
          }

          // Success - email confirmed
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to sign in...');

          // Sign out immediately after confirmation (user needs to sign in)
          await supabase?.auth?.signOut();

          // Redirect to sign-in page after 2 seconds
          setTimeout(() => {
            navigate('/institute-login');
          }, 2000);
        } 
        // Check if user is already authenticated (might have been auto-confirmed)
        else {
          const { data: { session } } = await supabase?.auth?.getSession();
          
          if (session) {
            // User is authenticated, email was confirmed
            setStatus('success');
            setMessage('Email verified successfully! Redirecting to sign in...');

            // Sign out so user can sign in
            await supabase?.auth?.signOut();

            setTimeout(() => {
              navigate('/institute-login');
            }, 2000);
          } else {
            // No valid tokens or session found
            setStatus('error');
            setMessage('Invalid confirmation link. Please try signing in.');
            setTimeout(() => {
              navigate('/institute-login');
            }, 3000);
          }
        }
      } catch (err) {
        console.error('Callback error:', err);
        setStatus('error');
        setMessage('An error occurred. Please try signing in.');
        setTimeout(() => {
          navigate('/institute-login');
        }, 3000);
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-xl border border-border p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Verifying Email
              </h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Email Verified!
              </h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mb-4">
                <Icon name="AlertCircle" size={32} color="var(--color-error)" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Verification Failed
              </h1>
              <p className="text-muted-foreground mb-4">{message}</p>
              <button
                onClick={() => navigate('/institute-login')}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
              >
                Go to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
